import { useState } from "react";
import { Button, Form, Header, Icon, Message, Segment } from "semantic-ui-react";
import { billingReportApi, ghApi } from "../../api/api";
import { useAuth } from "../../context/AuthContext";
import { hasCapability } from "../../utils/capabilities";

const currentMonthValue = () => new Date().toISOString().slice(0, 7);

const formatMonthLabel = (value) => {
  if (!value) return "";
  const [year, month] = value.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

function saveBlob({ blob, filename }) {
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

export default function ReportsTab() {
  const { currentUser } = useAuth();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <MonthlyDownload
        id="occupancy"
        icon="file excel outline"
        title="Occupancy Report"
        subheader="Room-by-day occupancy for a month, covering the guest house(s) you manage — one sheet per guest house, ready to download as Excel."
        download={ghApi.downloadOccupancyReport}
      />
      {hasCapability(currentUser, "bill.view") && (
        <MonthlyDownload
          id="billing-register"
          icon="rupee sign"
          title="Billing Register"
          subheader="Every bill finalized in the month with Rent, Boarding and the other headings in their own columns, a GST summary by rate, and the day's collections by payment mode — for the guest house(s) you manage."
          download={billingReportApi.downloadRegister}
        />
      )}
    </div>
  );
}

function MonthlyDownload({ id, icon, title, subheader, download }) {
  const [reportMonth, setReportMonth] = useState(currentMonthValue);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [downloadedFilename, setDownloadedFilename] = useState("");

  const handleMonthChange = (value) => {
    setReportMonth(value);
    setError("");
    setDownloadedFilename("");
  };

  const handleDownload = async () => {
    if (downloading || !reportMonth) return;
    setDownloading(true);
    setError("");
    setDownloadedFilename("");
    try {
      const file = await download(reportMonth);
      saveBlob(file);
      setDownloadedFilename(file.filename);
    } catch (err) {
      setError(err.message || "Failed to download report.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Segment padded="very">
      <Header as="h3" icon={icon} content={title} subheader={subheader} />

      <Form style={{ marginTop: 25 }}>
        <Form.Group>
          <Form.Input
            id={`${id}-month`}
            label="Month"
            type="month"
            value={reportMonth}
            onChange={(e) => handleMonthChange(e.target.value)}
            width={4}
          />
          <Form.Field>
            <label style={{ visibility: "hidden" }}>Download</label>
            <Button
              primary
              loading={downloading}
              disabled={downloading || !reportMonth}
              onClick={handleDownload}
            >
              <Icon name="download" />
              Download Report
            </Button>
          </Form.Field>
        </Form.Group>
      </Form>

      {downloadedFilename && (
        <Message
          positive
          icon="check circle outline"
          style={{ marginTop: 20 }}
          header={`Downloaded report for ${formatMonthLabel(reportMonth)}`}
          content={downloadedFilename}
        />
      )}
      {error && (
        <Message
          negative
          icon="warning circle"
          style={{ marginTop: 20 }}
          header="Couldn't download report"
          content={error}
        />
      )}
    </Segment>
  );
}
